import { useSearch } from "@tanstack/react-router";
import { useReducer } from "react";
import { StartFlowResponse } from "src/services/link/commands/NetTypes";
import { ConnectedEntity } from "src/types/apps";

export const initial: State = {
	step: 0,
	redirectUri: "",
	authToken: ""
};

export interface State {
	step: number;
	redirectUri: string;
	authToken: string;
	isNotContracted?: boolean;
	errorMessage?: string;
	entity?: ConnectedEntity;
}

enum ActionType {
	setStep = "setStep",
	onSelectEntity = "onSelectEntity",
	onStartFlow = "onStartFlow",
	onProductError = "onProductError",
	onErrorMessage = "onErrorMessage"
}

interface Action {
	type: string;
	val: number | ConnectedEntity | StartFlowResponse | string;
}

function reducer(state: State, action: Action): State {
	switch (action.type) {
		case ActionType.setStep:
			return { ...state, step: action.val as number };
		case ActionType.onSelectEntity:
			return { ...state, step: 2, entity: action.val as ConnectedEntity };
		case ActionType.onProductError:
			return { ...state, isNotContracted: true };
		case ActionType.onStartFlow:
			return {
				...state,
				entity: {
					...state.entity,
					id: state.entity?.id || "",
					maintenance: (action.val as StartFlowResponse).maintenance,
					name: state.entity?.name || ""
				},
				authToken: (action.val as StartFlowResponse).authToken,
				redirectUri: (action.val as StartFlowResponse).redirectUri
			};
		case ActionType.onErrorMessage:
			return { ...state, errorMessage: action.val as string };
		default:
			throw new Error();
	}
}

export default function useProductActionsStore() {
	const { entity }: { entity: string } = useSearch({
		strict: false
	});
	const initialEntity: ConnectedEntity | undefined = entity
		? { id: entity, name: entity }
		: undefined;

	const [state, dispatch] = useReducer(reducer, { ...initial, entity: initialEntity });

	const actions = {
		setStep: (val: number) => dispatch({ type: ActionType.setStep, val }),
		onSelectEntity: (val: ConnectedEntity) => dispatch({ type: ActionType.onSelectEntity, val }),
		onStartFlow: (val: StartFlowResponse) => dispatch({ type: ActionType.onStartFlow, val }),
		onProductError: () => dispatch({ type: ActionType.onProductError, val: 0 }),
		onErrorMessage: (val: string) => dispatch({ type: ActionType.onErrorMessage, val })
	};

	return [state, actions] as const;
}
